import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Returns a function that calls the passed in function after a state update.
 * Usage:
 * ```ts
 * const myFunc = useCallAfterStateUpdate(() => accessTheStateThatShouldBeFresh(state))
 * const someCallback = () => {
 *   setState(someNewState)
 *   myFunc()
 * }
 * ```
 * @param func The function that will be called after state has been updated.
 *             It can take any number of arguments, but the return value is ignored
 * @returns a new function that will call func after a state update
 */
export const useCallAfterStateUpdate = (func: (...args: any[]) => void) => {
	const [state, setState] = useState<{ call: boolean; args: any[] }>({
		call: false,
		args: [],
	})
	const { call, args } = state
	const callRef = useRef(false)

	useEffect(() => {
		if (call && call !== callRef.current) {
			func(...args)
			setState({ call: false, args: [] })
		}
		callRef.current = call
	}, [call, func, args])

	const funcWrapper = useCallback((...argsIn: any[]) => {
		setState({ call: true, args: [...argsIn] })
	}, [])

	return funcWrapper
}
